// ============================================================================
// SOP card — Kanban tile + off-board roll-up.
//
// Step 2 (2026-05-28) additions vs the original Phase-1 card:
//   * Facility-type pill (color-coded per spec)
//   * Days-until-start indicator (red past / amber ≤7d / green >7d)
//   * AM + BDD initials (when populated)
//   * Checklist completion ratio (e.g. "5/14 ✓") computed from JSONB
//   * Visual treatment unchanged for the existing market dot + stalled border
//     so muscle memory carries over.
// ============================================================================

import type {
  NewAccountSop,
  NewAccountSopFacilityType,
} from '../../shared/types';
import { daysUntilStart, parseStartDate } from './lib/lifecycle';
import { feeChip, usablesChip, type IntakeChip } from './intake-answers';
import { resolveCardName, SINGLE_SITE_CUSTOMER_LABEL } from './card-name';
import { useMarkets, marketColorPair } from '../../shared/markets';
import { hourlyCardRates, hourlyEconomics, perHourShort, rateBasisChip } from '../../shared/rate-basis';

interface Props {
  row: NewAccountSop;
  onClick: (row: NewAccountSop) => void;
}

export default function SopCard({ row, onClick }: Props) {
  const stalled = row.status === 'Stalled' || row.stage === 'Stalled';
  const cardName = resolveCardName(row);
  // The dot reads the market's own row, not the card's stored copy of it. The
  // copy is kept in step by a trigger and is still there for anything outside
  // the app, but reading the source means the dot cannot be a colour the
  // market stopped using.
  const { markets } = useMarkets();
  const marketColor = marketColorPair(markets, row.market_region).base;
  const marketLabel = row.market_region?.trim() || '—';
  const checklistTotals = countChecklistItems(row.checklist);
  const startInfo = buildStartInfo(row.start_date, row.lifecycle);
  const startChip = buildStartChip(row);
  const hourly = hourlyCardRates(row.service_lines);
  const hourlyEcon = hourlyEconomics(hourly?.client, hourly?.sub);

  return (
    <li>
      <button
        type="button"
        onClick={() => onClick(row)}
        className={`w-full rounded-lg border bg-tcs-raised p-3 text-left shadow-sm transition hover:border-tcs-border-strong hover:shadow ${
          stalled
            ? 'border-rag-red ring-1 ring-rag-red/40'
            : 'border-tcs-border'
        }`}
      >
        <div className="flex items-start gap-2">
          {/* Market dot — anchors the card the same way the urgency dot
              anchors hiring cards. */}
          <span
            className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: marketColor }}
            aria-label={`Market ${marketLabel}`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="truncate text-sm font-medium text-tcs-text">
                {cardName.primary}
              </h3>
              {stalled && (
                <span className="shrink-0 rounded bg-st-danger-bg-strong px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-st-danger-fg-strong">
                  Stalled
                </span>
              )}
            </div>

            {/* The customer, subordinate. Stated rather than repeated when the
                building IS the customer — the single-site case, which is most of
                them. Never promoted above the facility. */}
            <div className="mt-0.5 truncate text-xs text-tcs-text-muted">
              {cardName.customer ?? SINGLE_SITE_CUSTOMER_LABEL}
            </div>

            {/* ------ pill row ------ */}
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-tcs-text-muted">
              {row.facility_type && (
                <FacilityTypePill value={row.facility_type as NewAccountSopFacilityType} />
              )}

              <span
                className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                style={{
                  backgroundColor: tintFor(marketColor, 0.14),
                  color: marketColor,
                }}
              >
                {marketLabel}
              </span>

              {row.days_in_stage !== null && row.days_in_stage !== undefined && (
                <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] text-tcs-text-secondary">
                  {row.days_in_stage}d in stage
                </span>
              )}

              {checklistTotals.total > 0 && (
                <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] text-tcs-text-secondary">
                  {checklistTotals.done}/{checklistTotals.total} ✓
                </span>
              )}

              {startChip && (
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${startChip.cls}`}
                >
                  {startChip.text}
                </span>
              )}
            </div>

            {/* AFB-0055 — compact read-only economics (client · sub · profit ·
                margin). Only when a client price exists; margin tinted vs the
                26.5% target. Graceful when sub payment is unset — never a bogus
                100% margin. */}
            {/* Items 831/758: a card billed by the hour reads per hour, from
                its lines, with the Hourly chip — never a "monthly" figure. */}
            {hourly && (
              <div
                className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-tcs-text-muted"
                data-field="card_economics_hourly"
              >
                <span className="rounded-full bg-tcs-surface px-1.5 py-0.5 text-[10px] font-semibold text-tcs-text-secondary">
                  {rateBasisChip('hourly')}
                </span>
                <span title="What the client pays an hour">
                  Client{' '}
                  <span className="font-medium text-tcs-text-secondary">
                    {hourlyEcon.clientPerHour != null ? perHourShort(hourlyEcon.clientPerHour) : '—'}
                  </span>
                </span>
                <span aria-hidden>·</span>
                <span title="What the sub is paid an hour">
                  Sub{' '}
                  <span className="font-medium text-tcs-text-secondary">
                    {hourlyEcon.subPerHour != null ? perHourShort(hourlyEcon.subPerHour) : '—'}
                  </span>
                </span>
                {hourlyEcon.profitPerHour != null && hourlyEcon.marginPct != null && (
                  <>
                    <span aria-hidden>·</span>
                    <span title="Profit an hour">
                      Profit{' '}
                      <span className="font-medium text-tcs-text-secondary">
                        {perHourShort(hourlyEcon.profitPerHour)}
                      </span>
                    </span>
                    <span aria-hidden>·</span>
                    <span className={`font-semibold ${cardMarginClass(hourlyEcon.marginPct)}`} title="Margin %">
                      {hourlyEcon.marginPct.toFixed(1)}%
                    </span>
                  </>
                )}
              </div>
            )}
            {!hourly && typeof row.final_pricing === 'number' && row.final_pricing > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-tcs-text-muted">
                <span title="Client monthly price">
                  Client{' '}
                  <span className="font-medium text-tcs-text-secondary">
                    ${formatMoney(row.final_pricing)}
                  </span>
                </span>
                {typeof row.sub_payment === 'number' && row.sub_payment > 0 ? (
                  <>
                    <span aria-hidden>·</span>
                    <span title="Monthly sub payment">
                      Sub{' '}
                      <span className="font-medium text-tcs-text-secondary">
                        ${formatMoney(row.sub_payment)}
                      </span>
                    </span>
                    <span aria-hidden>·</span>
                    <span title="Profit / mo">
                      Profit{' '}
                      <span className="font-medium text-tcs-text-secondary">
                        ${formatMoney(row.final_pricing - row.sub_payment)}
                      </span>
                    </span>
                    <span aria-hidden>·</span>
                    <span
                      className={`font-semibold ${cardMarginClass(
                        ((row.final_pricing - row.sub_payment) / row.final_pricing) * 100,
                      )}`}
                      title="Margin %"
                    >
                      {(
                        ((row.final_pricing - row.sub_payment) / row.final_pricing) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  </>
                ) : (
                  <>
                    <span aria-hidden>·</span>
                    <span className="italic text-tcs-text-muted" title="Set sub payment to see margin">
                      set sub payment
                    </span>
                  </>
                )}
              </div>
            )}

            {/* AFB-0081 (Jose) — the two intake answers the board captured and
                then never showed back. Muted on a settled card, action-required
                while the card is still moving: see ./intake-answers for why 0%
                is an answer and why the 25 legacy Completed cards do not get a
                warning. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <IntakeAnswerChip chip={feeChip(row)} />
              <IntakeAnswerChip chip={usablesChip(row)} />
            </div>

            {/* ------ footer: start date + assignees ------ */}
            <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-tcs-text-muted">
              <span className={startInfo.tone}>{startInfo.text}</span>
              <div className="flex items-center gap-1.5">
                <Avatars bdd={(row as { bdd_name?: string }).bdd_name} am={(row as { am_name?: string }).am_name} />
              </div>
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

// AFB-0081. Three tones, and the middle one is the point: a missing answer is a
// task while the card is moving and history once it is settled. `title` carries
// the sentence that says which — the chip text alone cannot.
const INTAKE_CHIP_CLS: Record<IntakeChip['tone'], string> = {
  answered: 'bg-tcs-surface text-tcs-text-secondary',
  action: 'bg-st-warn-bg-strong text-st-warn-fg-strong font-semibold',
  muted: 'bg-tcs-surface text-tcs-text-muted italic',
};

function IntakeAnswerChip({ chip }: { chip: IntakeChip }) {
  return (
    <span
      title={chip.title}
      className={`rounded px-1.5 py-0.5 text-[10px] ${INTAKE_CHIP_CLS[chip.tone]}`}
    >
      {chip.text}
    </span>
  );
}


function tintFor(hex: string, alpha: number): string {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  const full =
    h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return '#F1F5F9';
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const FACILITY_TYPE_STYLES: Record<NewAccountSopFacilityType, string> = {
  Dialysis: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Surgery_Center: 'bg-purple-100 text-purple-800',
  Hospital: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
  MOB: 'bg-tcs-surface-strong text-tcs-text',
  Other: 'bg-tcs-surface text-tcs-text-secondary',
};

const FACILITY_TYPE_LABEL: Record<NewAccountSopFacilityType, string> = {
  Dialysis: 'Dialysis',
  Surgery_Center: 'Surgery Center',
  Hospital: 'Hospital',
  MOB: 'MOB',
  Other: 'Other',
};

function FacilityTypePill({ value }: { value: NewAccountSopFacilityType }) {
  const styles = FACILITY_TYPE_STYLES[value] ?? 'bg-tcs-surface text-tcs-text-secondary';
  const label = FACILITY_TYPE_LABEL[value] ?? String(value);
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${styles}`}>
      {label}
    </span>
  );
}

function Avatars({ bdd, am }: { bdd?: string; am?: string }) {
  const items: { label: string; initials: string }[] = [];
  if (bdd) items.push({ label: `BDD: ${bdd}`, initials: initialsOf(bdd) });
  if (am) items.push({ label: `AM: ${am}`, initials: initialsOf(am) });
  if (items.length === 0) return null;
  return (
    <span className="flex items-center -space-x-1.5">
      {items.map((i) => (
        <span
          key={i.label}
          title={i.label}
          aria-label={i.label}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-tcs-raised bg-tcs-surface text-[9px] font-semibold text-tcs-text-secondary"
        >
          {i.initials}
        </span>
      ))}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * AFB-0070 again, on the other view — fixed 2026-09-17.
 *
 * This built its own Date with `new Date(iso)`. ECMA-262 parses a date-only
 * string as UTC MIDNIGHT, so `.setHours(0,0,0,0)` then lands on the previous
 * calendar day anywhere west of Greenwich: every start date on the board came
 * out A DAY EARLY, and so did the `(Nd)` countdown beside it. Two buildings
 * starting 1 Oct read "Starts Sep 30".
 *
 * ⛔ The pill on the SAME CARD was right the whole time. It calls
 * `daysUntilStart`, which goes through `parseStartDate` and builds a LOCAL date
 * from the Y/M/D components. One card, two parses, two different answers about
 * what day it is. `sop-list.tsx` carries a comment claiming the Kanban card was
 * always correct for exactly this reason; that claim was false when it was
 * written down, and nobody looked at the board to check it.
 *
 * Both halves now come from `lib/lifecycle`, which is the one definition of
 * "the day this account starts".
 */
export function buildStartInfo(
  iso: string | null | undefined,
  /** The database's lifecycle for the card. A building past its planned date
   *  that nobody has confirmed has NOT started (Ben, 2026-09-25) — it is due. */
  lifecycle?: string | null,
): {
  text: string;
  tone: string;
} {
  if (!iso) return { text: 'No start date', tone: 'text-tcs-text-muted' };
  const start = parseStartDate(iso);
  const delta = daysUntilStart({ start_date: iso });
  if (!start || delta === null) {
    return { text: 'No start date', tone: 'text-tcs-text-muted' };
  }
  const dateLabel = start.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  if (delta < 0 && lifecycle === 'behind') return { text: `Due ${dateLabel}`, tone: 'text-rag-red' };
  if (delta < 0) return { text: `Started ${dateLabel}`, tone: 'text-rag-red' };
  if (delta === 0) return { text: `Starts today`, tone: 'text-st-warn-fg' };
  if (delta <= 7) return { text: `Starts ${dateLabel} (${delta}d)`, tone: 'text-st-warn-fg' };
  return { text: `Starts ${dateLabel}`, tone: 'text-rag-green' };
}

/**
 * Cowork 02: a prominent start-date chip the card surfaces above the footer.
 *   * Confirmed-started but lingering on the board (open KEY info) → amber
 *     "Started — confirm info".
 *   * Planned start today → red "Starts today".
 *   * Planned start passed, unconfirmed → red "Overdue start — confirm started?".
 *   * Otherwise no chip (the footer already shows the countdown).
 */
function buildStartChip(
  row: NewAccountSop,
): { text: string; cls: string } | null {
  if (row.started_confirmed_at) {
    return {
      text: 'Started — confirm info',
      cls: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
    };
  }
  const n = daysUntilStart({ start_date: row.start_date });
  if (n === null) return null;
  if (n < 0) {
    return {
      text: 'Overdue start — confirm started?',
      cls: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
    };
  }
  if (n === 0) {
    return { text: 'Starts today', cls: 'bg-st-danger-bg-strong text-st-danger-fg-strong' };
  }
  return null;
}

function countChecklistItems(checklist: unknown): { done: number; total: number } {
  if (!checklist || typeof checklist !== 'object') return { done: 0, total: 0 };
  let done = 0;
  let total = 0;
  for (const v of Object.values(checklist as Record<string, unknown>)) {
    if (!Array.isArray(v)) continue;
    for (const item of v) {
      if (item && typeof item === 'object' && 'checked' in (item as Record<string, unknown>)) {
        total += 1;
        if ((item as { checked?: boolean }).checked) done += 1;
      }
    }
  }
  return { done, total };
}

// AFB-0055 — stoplight for the card margin %, tinted against the 26.5% target
// (green ≥ target, amber between 0 and target, red ≤ 0). Mirrors project-work.
function cardMarginClass(m: number): string {
  if (!Number.isFinite(m)) return 'text-tcs-text-muted';
  if (m <= 0) return 'text-rag-red';
  if (m >= 26.5) return 'text-rag-green';
  return 'text-st-warn-fg';
}

function formatMoney(n: number): string {
  return Math.round(n).toLocaleString();
}
